import { useMemo, useState } from 'react';

import type { ProductStatus } from '@/shared/api/types/generated';
import { ProductItemData } from '@/shared/types/product';

import type { ProductFilters } from '../types';

interface UseProductFeedDataParams {
  allProducts: ProductItemData[];
  status: ProductStatus;
}

interface UseProductFeedDataReturn {
  products: ProductItemData[];
  availableBrands: { brand: string; count: number }[];
  filters: ProductFilters;
  setFilters: (filters: ProductFilters) => void;
}

// Filtres locaux de vue (marque + tri) — les attributs d'articles (état,
// taille, couleur, prix) sont filtrés en amont par le segment global.
export const useProductFeedData = ({ allProducts, status }: UseProductFeedDataParams): UseProductFeedDataReturn => {
  const [filters, setFilters] = useState<ProductFilters>({
    sort: 'newest', // Par défaut: plus récent
  });

  // Filtrer et trier côté client
  const filteredProducts = useMemo(() => {
    let productsData = [...allProducts];

    // Filtrer par marques (multi-sélection — vide = toutes)
    if (filters.brands?.length) {
      productsData = productsData.filter((p) => p.brand && filters.brands!.includes(p.brand));
    }

    // Trier
    if (filters.sort) {
      productsData = productsData.sort((a, b) => {
        switch (filters.sort) {
          case 'newest':
            return (
              new Date(status === 'sold' ? (b.soldAt ?? 0) : (b.uploadedAt ?? 0)).getTime() -
              new Date(status === 'sold' ? (a.soldAt ?? 0) : (a.uploadedAt ?? 0)).getTime()
            );
          case 'oldest':
            return (
              new Date(status === 'sold' ? (a.soldAt ?? 0) : (a.uploadedAt ?? 0)).getTime() -
              new Date(status === 'sold' ? (b.soldAt ?? 0) : (b.uploadedAt ?? 0)).getTime()
            );
          case 'priceAsc':
            return (a.price ?? 0) - (b.price ?? 0);
          case 'priceDesc':
            return (b.price ?? 0) - (a.price ?? 0);
          default:
            return 0;
        }
      });
    }

    return productsData;
  }, [allProducts, filters, status]);

  // Marques disponibles (avec compte), calculées sur l'ensemble des produits
  // du segment — le filtre marque ne se restreint pas lui-même.
  // Triées par nombre de produits décroissant (les plus représentées d'abord).
  const availableBrands = useMemo(() => {
    const brandCounts = new Map<string, number>();
    allProducts.forEach((product) => {
      if (product.brand) {
        brandCounts.set(product.brand, (brandCounts.get(product.brand) ?? 0) + 1);
      }
    });
    return Array.from(brandCounts.entries())
      .map(([brand, count]) => ({ brand, count }))
      .sort((a, b) => b.count - a.count || a.brand.localeCompare(b.brand));
  }, [allProducts]);

  return {
    products: filteredProducts,
    availableBrands,
    filters,
    setFilters,
  };
};
